import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatFieldValue,
  formatSelectValue,
  shouldSkipInListView,
  isFileFieldType,
} from '@iqvia-technology-solutions/formcrafter';
import FileAttachmentHandeling from '../FileAttachmentHandeling';
import {
  formatDateValue,
  formatHiddenFieldValue,
  formatSwitchValue,
  formatTextareaValue,
  isBase64Signature,
  getBase64ImageSrc,
  isDateFieldType,
  SWITCH_VALUES,
} from '../../../../utils/formCrafter/listViewUtils';
import styles from '../FormCrafterListView.module.css';

/**
 * EmptyValue - Reusable empty/null value display (DRY)
 */
const EmptyValue = () => <span className={styles.fieldValue}>-</span>;

/**
 * Renders file attachments (server files or local File objects)
 */
const FileAttachments = ({ files, allowDeletedFiles = false }) => (
  <div className={styles.fileAttachments}>
    {files.map((file, index) => {
      // Handle local File objects (newly uploaded)
      if (file instanceof File) {
        const localFile = {
          id: `local-${index}-${file.name}`,
          name: file.name,
          mimeType: file.type,
          isLocalFile: true,
          file: file,
        };
        return <FileAttachmentHandeling key={localFile.id} file={localFile} />;
      }
      // Handle server files
      return (
        <FileAttachmentHandeling
          key={file.id || index}
          file={file}
          allowDeletedFiles={allowDeletedFiles}
        />
      );
    })}
  </div>
);

/**
 * Renders signature field (base64 or file)
 * Priority: base64 value (inline edit) > server files (original)
 */
const SignatureField = ({ value, files, t }) => {
  // Check for base64 signature FIRST (inline edit takes priority over server files)
  if (
    value &&
    typeof value === 'string' &&
    value.trim() !== '' &&
    isBase64Signature(value)
  ) {
    return (
      <div className={styles.fileAttachments}>
        <img
          src={getBase64ImageSrc(value)}
          alt={t('fieldValueRenderer.signature')}
          className={styles.signatureImage}
        />
      </div>
    );
  }

  // Fall back to server files if no base64 value
  if (files && files.length > 0) {
    return <FileAttachments files={files} />;
  }

  return <EmptyValue />;
};

/**
 * Renders textarea with line breaks preserved
 */
const TextareaField = ({ value }) => {
  const textValue = formatTextareaValue(value);
  if (textValue === '-') {
    return <EmptyValue />;
  }

  const formattedText = textValue.split('\n').map((line, index, array) => (
    <React.Fragment key={index}>
      {line}
      {index < array.length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div className={`${styles.fieldValue} ${styles.textareaValue}`}>
      {formattedText}
    </div>
  );
};

const formatPriceDisplayValue = (value) => {
  if (!value) return '-';
  if (typeof value === 'object') {
    const currency = value.currency || '';
    const amountValue = value.amount ?? '';
    const numericAmount =
      typeof amountValue === 'number' ? amountValue : Number(amountValue);
    if (!Number.isNaN(numericAmount)) {
      return `${currency} ${numericAmount.toFixed(2)}`.trim();
    }
    if (
      amountValue !== '' &&
      amountValue !== null &&
      amountValue !== undefined
    ) {
      return `${currency} ${amountValue}`.trim();
    }
    return currency || '-';
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const numericAmount = Number(value);
    if (!Number.isNaN(numericAmount)) {
      return numericAmount.toFixed(2);
    }
    return String(value);
  }
  return '-';
};

/**
 * FieldValueRenderer - Renders field values based on type
 *
 * @param {Object} props
 * @param {string|number} props.subformId - Subform ID
 * @param {string} props.fieldId - Field ID
 * @param {*} props.value - Field value
 * @param {string} props.fieldType - Field type
 * @param {Object} props.field - Field schema object
 * @param {Object} props.subformData - Subform data for context
 * @param {Array} props.files - File metadata for this field (already filtered by parent)
 * @param {boolean} props.showHiddenFields - Whether to show hidden fields
 */
const FieldValueRenderer = ({
  subformId,
  fieldId,
  value,
  fieldType,
  field,
  subformData = null,
  files,
  showHiddenFields = false,
  allowDeletedFiles = false,
}) => {
  const { t } = useTranslation();
  // Skip divider and container fields
  if (shouldSkipInListView(fieldType)) {
    return null;
  }

  // Handle hidden fields
  if (fieldType === 'hidden') {
    if (!showHiddenFields) return null;
    const displayValue = formatHiddenFieldValue(value);
    return <span className={styles.fieldValue}>{displayValue}</span>;
  }

  // Handle heading fields
  if (fieldType === 'heading') {
    const Tag = field?.headingTag || 'h3';
    return (
      <Tag className={styles.headingTag}>{field?.label || value || '-'}</Tag>
    );
  }

  // Handle paragraph fields
  if (fieldType === 'paragraph') {
    const Tag = field?.paragraphType || 'p';
    return (
      <Tag className={styles.paragraphTag}>{field?.label || value || '-'}</Tag>
    );
  }

  // Handle signature field
  if (fieldType === 'signature') {
    return <SignatureField value={value} files={files} t={t} />;
  }

  // Handle file fields
  const isFileField = isFileFieldType(fieldType);
  if (isFileField) {
    // Collect new File objects from value
    const valueArray = Array.isArray(value) ? value : [];
    const newFiles = valueArray.filter((item) => item instanceof File);
    // Server files are already filtered by parent via filteredFiles
    const serverFiles = files && files.length > 0 ? files : [];

    // Combine server files AND new files - show both together
    const allFiles = [...serverFiles, ...newFiles];
    if (allFiles.length > 0) {
      return (
        <FileAttachments
          files={allFiles}
          allowDeletedFiles={allowDeletedFiles}
        />
      );
    }
    // Handle other value types
    if (value && value !== '' && value !== null) {
      if (typeof value === 'string') {
        return <span className={styles.fieldValue}>{value}</span>;
      }
      if (Array.isArray(value) && value.length > 0) {
        return (
          <span className={styles.fieldValue}>
            {t('fieldValueRenderer.fileCount', { count: value.length })}
          </span>
        );
      }
    }
    return <EmptyValue />;
  }

  // Handle date fields
  if (isDateFieldType(fieldType)) {
    const dateValue = value || field?.defaultValue;
    const formattedDate = formatDateValue(dateValue, fieldType);
    return <span className={styles.fieldValue}>{formattedDate}</span>;
  }

  // Handle select fields
  if (fieldType === 'select') {
    return (
      <span className={styles.fieldValue}>
        {formatSelectValue(value, field)}
      </span>
    );
  }

  // Handle checkbox and radio fields
  if (fieldType === 'checkbox' || fieldType === 'radio') {
    return (
      <span className={styles.fieldValue}>
        {formatSelectValue(value, field)}
      </span>
    );
  }

  // Handle switch fields
  if (fieldType === 'switch') {
    const switchValue = formatSwitchValue(value);
    if (switchValue === SWITCH_VALUES.EMPTY) {
      return <EmptyValue />;
    }
    return (
      <span className={styles.fieldValue}>
        {t(`fieldValueRenderer.switch.${switchValue}`)}
      </span>
    );
  }

  // Handle textarea fields
  if (fieldType === 'textarea') {
    return <TextareaField value={value} />;
  }

  // Handle price fields
  if (fieldType === 'price') {
    const formattedPrice = formatPriceDisplayValue(value);
    if (formattedPrice === '-') {
      return <EmptyValue />;
    }
    return <span className={styles.fieldValue}>{formattedPrice}</span>;
  }

  // Regular field value
  return (
    <span className={styles.fieldValue}>
      {formatFieldValue(value, field, subformData)}
    </span>
  );
};

export default FieldValueRenderer;
